import { useCallback, useMemo, useState } from 'react';

import { emptyObject } from 'ui-components/default-props/empty';

const buildShippingServiceRecommendationKey = (fromSellerProviderId, fromServiceId, toServiceId) => {
  return `${fromSellerProviderId}-${fromServiceId}-${toServiceId}`;
};

/**
 * @param {ServiceRateRecommendationSummary[]} summaryShippingServiceRecommendations
 * @returns {CostReviewShippingServiceRecommendationMap}
 */
const buildShippingServiceRecommendations = (summaryShippingServiceRecommendations) => {
  return summaryShippingServiceRecommendations.reduce((acc, rec) => {
    const { fromServiceId, toServiceId, totalSavings, fromSellerProviderId, toSellerProviderId } = rec;

    return {
      ...acc,
      [fromSellerProviderId]: {
        ...acc[fromSellerProviderId],
        [fromServiceId]: {
          ...acc[fromSellerProviderId]?.[fromServiceId],
          [toServiceId]: {
            key: buildShippingServiceRecommendationKey(fromSellerProviderId, fromServiceId, toServiceId),
            fromSellerProviderId: `${fromSellerProviderId}`,
            fromServiceId: `${fromServiceId}`,
            toSellerProviderId: `${toSellerProviderId}`,
            toServiceId: `${toServiceId}`,
            totalSavings,
          },
        },
      },
    };
  }, {});
};

/**
 * @typedef {object} CostReviewShippingServiceRecommendations
 * @property {CostReviewSelectedRecommendations} selectedShippingServiceRecommendations
 * @property {CostReviewShippingServiceRecommendationMap} shippingServiceRecommendations
 * @property {(recommendationKey: string) => void} toggleShippingServiceRecommendation
 */

/**
 * @param {ServiceRateRecommendationSummary[]} shippingServiceRecommendationSummary
 * @returns {CostReviewShippingServiceRecommendations}
 */
export const useCostReviewShippingServiceRecommendations = (shippingServiceRecommendationSummary) => {
  const [selectedShippingServiceRecommendations, setSelectedShippingServiceRecommendations] = useState({});

  const shippingServiceRecommendations = useMemo(() => {
    return shippingServiceRecommendationSummary ? buildShippingServiceRecommendations(shippingServiceRecommendationSummary) : emptyObject;
  }, [shippingServiceRecommendationSummary]);

  const toggleShippingServiceRecommendation = useCallback((recommendationKey) => {
    setSelectedShippingServiceRecommendations((prev) => {
      return {
        ...prev,
        [recommendationKey]: !prev[recommendationKey],
      };
    });
  }, []);

  const selectAllShippingServiceRecommendations = useCallback(() => {
    const allRecommendationsSelected = shippingServiceRecommendationSummary.reduce((acc, recommendation) => {
      const recommendationKey = buildShippingServiceRecommendationKey(
        recommendation.fromSellerProviderId,
        recommendation.fromServiceId,
        recommendation.toServiceId,
      );
      return {
        ...acc,
        [recommendationKey]: true,
      };
    }, {});

    setSelectedShippingServiceRecommendations(allRecommendationsSelected);
  }, [shippingServiceRecommendationSummary]);

  return {
    selectedShippingServiceRecommendations,
    shippingServiceRecommendations,
    toggleShippingServiceRecommendation,
    selectAllShippingServiceRecommendations,
  };
};
